import {
  useWSModalCustom,
  WSAlert,
  WSCard,
  WSControl,
  WSFlexBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSPill,
  WSPillProps
} from "@wingspanhq/fe-component-library";
import { useEffect, useMemo, useState } from "react";
import { LabelMethod } from "../../modules/Settings/components/LabelMethod";
import { useDefaultPaymentMethod } from "../../modules/Settings/components/payment/DefaultPaymentMethod";
import { queriesPayees } from "@query";
import { useXWingspanPayerId } from "../../modules/Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { selectorPayeeDisplayName } from "../../shared/selectors/selectorPayeeDisplayName";

type Props = { payeeId: string } & WSModalCustomProps<any>;

export const ModalAutopaySettings: React.FC<Props> = ({
  payeeId,
  ...modalProps
}) => {
  const xWingspanAccount = useXWingspanPayerId();
  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId,
    xWingspanAccount
  });

  const payeeName = useMemo(
    () =>
      queryPayee.data ? selectorPayeeDisplayName(queryPayee.data) : undefined,
    [queryPayee.data]
  );

  const paymentMethod = useDefaultPaymentMethod();

  const [autopayeEnabled, setAutopayEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (!queryPayee.data) {
      return;
    }

    setAutopayEnabled(!!queryPayee.data.defaults?.isAutoPayEnabled);
  }, [queryPayee.data]);

  const [save, saveMeta] = queriesPayees.updatePayee.useMutation({
    onSuccess: () => {
      modalProps.onClose(undefined);
    }
  });

  return (
    <WSModal
      title={`Autopay settings for ${payeeName}`}
      size="M"
      {...modalProps}
      divider
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: () => {
              modalProps.onClose(undefined);
            }
          },
          {
            loading: saveMeta.isLoading,
            label: "Save changes",
            onClick: () => {
              save({
                payeeId,
                payeeUpdate: {
                  defaults: { isAutoPayEnabled: autopayeEnabled }
                },
                xWingspanAccount
              });
            }
          }
        ]
      }}
    >
      <WSList gap="2XL">
        <WSCard>
          <WSFlexBox wrap="nowrap" justify="space-between" alignItems="center">
            {paymentMethod.id && paymentMethod.type ? (
              <LabelMethod id={paymentMethod.id} type={paymentMethod.type} />
            ) : null}

            <WSFlexBox wrap="nowrap" gap="S" alignItems="center">
              {autopayeEnabled ? (
                <WSPill {...pillAutopayOn} />
              ) : (
                <WSPill {...pillAutopayOff} />
              )}
              <WSControl
                type="switch"
                value={autopayeEnabled}
                onChange={setAutopayEnabled}
                label="Autopay"
              />
            </WSFlexBox>
          </WSFlexBox>
        </WSCard>
        <WSAlert theme="info" icon="info-circle" title="About autopay">
          Turning autopay on will charge your chosen payment method on the due
          date of each invoice this contractor sends.
        </WSAlert>
      </WSList>
    </WSModal>
  );
};

export const useModalAutopaySettings = () =>
  useWSModalCustom(ModalAutopaySettings);

export const pillAutopayOn: WSPillProps = {
  theme: "success",
  text: "On",
  badge: true
};

export const pillAutopayOff: WSPillProps = {
  text: "Off",
  badge: true
};
